import { useField } from 'formik'
import React, { useEffect, useState } from 'react'
import {
  ErrorText,
  InputContainer,
  InputWrapper,
  Label,
  OrderNumberPart,
  OutletOverlay,
  OutletPart,
  StyledInput,
} from './SupportFormStyle'

interface OrderNumberInputProps {
  name: string
  label: string
  placeholder?: string
  required?: boolean
  onOrderValidate?: (
    orderNumber: string
  ) => Promise<{ outletName?: string; isValid?: boolean }>
  onValidationChange?: (isValid: boolean) => void
}

export const OrderNumberInput: React.FC<OrderNumberInputProps> = ({
  name,
  label,
  placeholder,
  required,
  onOrderValidate,
  onValidationChange,
}) => {
  const [field, meta, helpers] = useField(name)
  const [outletName, setOutletName] = useState<string>('')
  const [hasValidated, setHasValidated] = useState(false)

  useEffect(() => {
    let didCancel = false
    if (!field.value?.trim()) {
      onValidationChange?.(false)
      return
    }
    const validateInitialValue = async () => {
      try {
        const result = await onOrderValidate?.(field.value.trim())
        if (!didCancel) {
          setOutletName(result?.outletName || '')
          onValidationChange?.(result?.isValid || false)
        }
      } catch {
        if (!didCancel) {
          helpers.setError('Unable to validate order number:')
          onValidationChange?.(false)
        }
      }
    }
    void validateInitialValue()

    return () => {
      didCancel = true
    }
  }, [])

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e)
    setHasValidated(true)

    if (onOrderValidate && e.target.value.trim()) {
      try {
        const result = await onOrderValidate(e.target.value.trim())

        if (result?.isValid) {
          setOutletName(result?.outletName || '')
          helpers.setError(undefined)
          onValidationChange?.(true)
        } else {
          setOutletName('')
          onValidationChange?.(false)
          if (required && e.target.value.trim()) {
            helpers.setError(
              'Order number not found. Please check and try again.'
            )
          }
        }
      } catch {
        setOutletName('')
        onValidationChange?.(false)
        if (required && e.target.value.trim()) {
          helpers.setError('Unable to validate order number. Please try again.')
        }
      }
    } else {
      setOutletName('')
      onValidationChange?.(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    helpers.setValue(e.target.value)
    if (outletName) {
      setOutletName('')
    }
    onValidationChange?.(false)
    if (hasValidated && meta.error) {
      helpers.setError(undefined)
    }
  }

  const handleFocus = () => {
    setOutletName('')
    onValidationChange?.(false)
  }

  return (
    <InputContainer>
      <Label htmlFor={name}>
        {label}
        {required && <span style={{ color: '#ef4444' }}> *</span>}
      </Label>
      <InputWrapper>
        <StyledInput
          id={name}
          name={name}
          type="text"
          placeholder={placeholder}
          value={field.value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          autoComplete="off"
        />
        {outletName && (
          <OutletOverlay>
            <OrderNumberPart>{field.value}</OrderNumberPart>
            <OutletPart>{outletName}</OutletPart>
          </OutletOverlay>
        )}
      </InputWrapper>
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </InputContainer>
  )
}
